import React, { useState, useEffect, useRef } from 'react';
import styled, { keyframes } from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import Config from '../api/Config';

/* ─── Constants ─── */
// Derive from the same source Config uses for its requests, so this can
// never drift out of sync with the axios instance's baseURL.
const BASE_URL = (
  Config?.defaults?.baseURL ||
  process.env.REACT_APP_API_URL ||
  'https://berrybloomer.com'
).replace(/\/$/, '');

const DEFAULT_IMAGE = `data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='400' height='300'%3E%3Crect width='400' height='300' fill='%231e293b'/%3E%3Ctext x='50%25' y='50%25' dominant-baseline='middle' text-anchor='middle' fill='%2364748b' font-size='14' font-family='sans-serif'%3ENo Image%3C/text%3E%3C/svg%3E`;
const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB

const encodeSegments = (path) =>
  path.replace(/^\/+/, '').split('/').map(encodeURIComponent).join('/');

/**
 * Returns an ordered list of URLs to try for a given project record.
 * The original single "/webapp/application/{id}/image" guess was never
 * confirmed against the backend, so this tries several structurally
 * different shapes — same approach used for client logos — until one
 * actually loads. Once you confirm the real path in the Network tab,
 * move it to the front of this list and drop the rest.
 */
const buildImageCandidates = (project) => {
  const id = project?.id;
  if (id == null) return [DEFAULT_IMAGE];

  let origin = null;
  try {
    origin = new URL(BASE_URL).origin;
  } catch {
    origin = null;
  }

  const candidates = [];
  // Original guess (kept first in case it's actually right)
  candidates.push(`${BASE_URL}/webapp/application/${id}/image`);
  // Matches the pattern confirmed working for /client-logo/{id}/image
  candidates.push(`${BASE_URL}/application/${id}/image`);
  // In case images are served from the API root without the /api prefix
  if (origin) candidates.push(`${origin}/application/${id}/image`);
  if (origin) candidates.push(`${origin}/webapp/application/${id}/image`);
  // In case a filePath/fileName field exists on the project record itself
  if (project?.filePath) {
    candidates.push(`${BASE_URL}/application/image/${encodeSegments(project.filePath)}`);
  }
  if (project?.fileName) {
    candidates.push(`${BASE_URL}/application/image/${encodeSegments(project.fileName)}`);
  }

  return candidates.length ? candidates : [DEFAULT_IMAGE];
};

/* ─── Animations ─── */
const shimmer = keyframes`
  0%   { background-position: -400px 0; }
  100% { background-position:  400px 0; }
`;

const fadeIn = {
  hidden:  { opacity: 0, y: 16 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.3 } },
  exit:    { opacity: 0, y: -8, transition: { duration: 0.2 } },
};

/* ─── Styled Components ─── */
const Page = styled.div`
  min-height: 100vh;
  padding: 48px 28px 80px;
  font-family: 'Poppins', sans-serif;
  color: #e2e8f0;
  max-width: 1200px;
  margin: 0 auto;
`;

const TopBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 36px;
  flex-wrap: wrap;
  gap: 12px;
`;

const PageTitle = styled.h1`
  font-size: 1.8rem;
  font-weight: 800;
  background: linear-gradient(135deg, #fff 30%, #00c4ff);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
`;

const AddBtn = styled.button`
  background: linear-gradient(135deg, #5f2eea, #0a84ff);
  color: #fff;
  border: none;
  padding: 10px 22px;
  border-radius: 24px;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition: opacity 0.2s;
  &:hover { opacity: 0.88; }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 24px;
`;

const Card = styled(motion.div)`
  border-radius: 18px;
  overflow: hidden;
  background: linear-gradient(160deg, #0f172a, #1e293b);
  border: 1px solid rgba(148, 163, 184, 0.08);
  box-shadow: 0 4px 20px rgba(0,0,0,0.3);
  display: flex;
  flex-direction: column;
`;

const CardImg = styled.div`
  width: 100%;
  aspect-ratio: 4/3;
  background: #0a1120;
  overflow: hidden;
  position: relative;
  cursor: ${({ $clickable }) => ($clickable ? 'pointer' : 'default')};

  &:hover .link-overlay {
    opacity: ${({ $clickable }) => ($clickable ? 1 : 0)};
  }
`;

const Img = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
  transition: opacity 0.3s, transform 0.3s;
  opacity: ${({ $loaded }) => ($loaded ? 1 : 0)};

  ${CardImg}:hover & {
    transform: ${({ $clickable }) => ($clickable ? 'scale(1.04)' : 'none')};
  }
`;

const ImgShimmer = styled.div`
  position: absolute;
  inset: 0;
  background: linear-gradient(90deg, #1e293b 25%, #243044 50%, #1e293b 75%);
  background-size: 800px 100%;
  animation: ${shimmer} 1.6s infinite linear;
`;

const BrokenBadge = styled.div`
  position: absolute;
  top: 8px;
  left: 8px;
  right: 8px;
  background: rgba(220, 38, 38, 0.9);
  color: #fff;
  font-size: 0.65rem;
  font-weight: 600;
  padding: 4px 8px;
  border-radius: 6px;
  z-index: 2;
  word-break: break-all;
`;

const LinkOverlay = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  background: rgba(10, 17, 32, 0.55);
  color: #fff;
  font-size: 0.85rem;
  font-weight: 600;
  opacity: 0;
  transition: opacity 0.2s;
  pointer-events: none;
`;

const CardBody = styled.div`
  padding: 16px 18px 20px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex: 1;
`;

const CardTitle = styled.h3`
  font-size: 1rem;
  font-weight: 700;
  color: #f1f5f9;
  margin: 0;
`;

const CardDesc = styled.p`
  font-size: 0.82rem;
  color: #64748b;
  line-height: 1.6;
  margin: 0;
  flex: 1;
`;

const CardLink = styled.a`
  font-size: 0.78rem;
  color: #00c4ff;
  text-decoration: none;
  word-break: break-all;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  &:hover { text-decoration: underline; }
`;

const CardActions = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 12px;
`;

const ActionBtn = styled.button`
  flex: 1;
  padding: 8px;
  border-radius: 10px;
  border: none;
  font-size: 0.8rem;
  font-weight: 600;
  cursor: pointer;
  transition: opacity 0.2s;
  background: ${({ $variant }) =>
    $variant === 'delete' ? 'rgba(248, 113, 113, 0.12)' : 'rgba(0, 196, 255, 0.1)'};
  color: ${({ $variant }) => ($variant === 'delete' ? '#f87171' : '#00c4ff')};
  border: 1px solid ${({ $variant }) =>
    $variant === 'delete' ? 'rgba(248,113,113,0.25)' : 'rgba(0,196,255,0.2)'};
  &:hover { opacity: 0.75; }
`;

const StatusText = styled.p`
  color: ${({ $error }) => ($error ? '#f87171' : '#475569')};
  font-size: 0.9rem;
  margin: 40px 0;
  text-align: center;
`;

const Overlay = styled(motion.div)`
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.65);
  z-index: 9000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
`;

const Modal = styled(motion.div)`
  background: #0f172a;
  border: 1px solid rgba(148,163,184,0.12);
  border-radius: 20px;
  padding: 32px;
  width: 100%;
  max-width: 480px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 24px 60px rgba(0,0,0,0.5);
`;

const ModalTitle = styled.h2`
  font-size: 1.2rem;
  font-weight: 700;
  color: #f1f5f9;
  margin: 0 0 24px;
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-bottom: 16px;
`;

const Label = styled.label`
  font-size: 0.8rem;
  font-weight: 600;
  color: #94a3b8;
  text-transform: uppercase;
  letter-spacing: 0.06em;
`;

const Input = styled.input`
  background: #1e293b;
  border: 1px solid rgba(148,163,184,0.15);
  border-radius: 10px;
  padding: 10px 14px;
  color: #e2e8f0;
  font-size: 0.9rem;
  font-family: 'Poppins', sans-serif;
  outline: none;
  transition: border-color 0.2s;
  &:focus { border-color: rgba(0,196,255,0.4); }
`;

const Textarea = styled.textarea`
  background: #1e293b;
  border: 1px solid rgba(148,163,184,0.15);
  border-radius: 10px;
  padding: 10px 14px;
  color: #e2e8f0;
  font-size: 0.9rem;
  font-family: 'Poppins', sans-serif;
  outline: none;
  resize: vertical;
  min-height: 80px;
  transition: border-color 0.2s;
  &:focus { border-color: rgba(0,196,255,0.4); }
`;

const FileInput = styled.input`
  font-size: 0.85rem;
  color: #94a3b8;
  background: #1e293b;
  border: 1px dashed rgba(148,163,184,0.25);
  border-radius: 10px;
  padding: 10px 14px;
  cursor: pointer;
  width: 100%;
  &::file-selector-button {
    background: rgba(0,196,255,0.1);
    color: #00c4ff;
    border: 1px solid rgba(0,196,255,0.2);
    border-radius: 6px;
    padding: 4px 10px;
    font-size: 0.8rem;
    cursor: pointer;
    margin-right: 10px;
  }
`;

const PreviewImg = styled.img`
  margin-top: 10px;
  width: 100%;
  aspect-ratio: 4/3;
  object-fit: cover;
  border-radius: 10px;
  border: 1px solid rgba(148,163,184,0.15);
`;

const ModalActions = styled.div`
  display: flex;
  gap: 10px;
  margin-top: 24px;
`;

const ModalBtn = styled.button`
  flex: 1;
  padding: 10px;
  border-radius: 12px;
  border: none;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition: opacity 0.2s;
  background: ${({ $primary }) =>
    $primary ? 'linear-gradient(135deg,#5f2eea,#0a84ff)' : 'rgba(148,163,184,0.1)'};
  color: ${({ $primary }) => ($primary ? '#fff' : '#94a3b8')};
  &:hover    { opacity: 0.85; }
  &:disabled { opacity: 0.45; cursor: not-allowed; }
`;

const ErrMsg = styled.p`
  color: #f87171;
  font-size: 0.8rem;
  margin-top: 8px;
`;

const ConfirmText = styled.p`
  color: #94a3b8;
  font-size: 0.9rem;
  margin: 0 0 24px;
  line-height: 1.6;
`;

const SkeletonCard = styled.div`
  border-radius: 18px;
  overflow: hidden;
  background: linear-gradient(160deg, #0f172a, #1e293b);
  border: 1px solid rgba(148,163,184,0.06);
  .sk-img {
    aspect-ratio: 4/3;
    background: linear-gradient(90deg, #1e293b 25%, #243044 50%, #1e293b 75%);
    background-size: 800px 100%;
    animation: ${shimmer} 1.6s infinite linear;
  }
  .sk-body { padding: 16px 18px 20px; display: flex; flex-direction: column; gap: 10px; }
  .sk-line {
    height: 12px; border-radius: 6px;
    background: linear-gradient(90deg, #1e293b 25%, #243044 50%, #1e293b 75%);
    background-size: 800px 100%;
    animation: ${shimmer} 1.6s infinite linear;
  }
`;

/* ─── ProjectCard ─── */
function ProjectCard({ project, onEdit, onDelete }) {
  const [loaded, setLoaded] = useState(false);
  const [broken, setBroken] = useState(false);
  const candidates = useRef(buildImageCandidates(project)).current;
  const [attemptIndex, setAttemptIndex] = useState(0);
  const src = candidates[attemptIndex] ?? DEFAULT_IMAGE;
  const hasLink = Boolean(project.link && project.link.trim());

  const handleImageClick = () => {
    if (hasLink) {
      window.open(project.link, '_blank', 'noopener,noreferrer');
    }
  };

  const handleImageKeyDown = (e) => {
    if (hasLink && (e.key === 'Enter' || e.key === ' ')) {
      e.preventDefault();
      handleImageClick();
    }
  };

  return (
    <Card variants={fadeIn} initial="hidden" animate="visible" exit="exit" layout>
      <CardImg
        $clickable={hasLink}
        onClick={handleImageClick}
        role={hasLink ? 'button' : undefined}
        tabIndex={hasLink ? 0 : undefined}
        onKeyDown={handleImageKeyDown}
        title={hasLink ? 'Open project link' : undefined}
        aria-label={hasLink ? `Open link for ${project.name}` : undefined}
      >
        {!loaded && <ImgShimmer />}
        {/* Surfaces the failure instead of hiding it behind a silent
            placeholder swap, once every candidate URL has been tried. */}
        {broken && (
          <BrokenBadge title={candidates.join(' | ')}>
            404 (tried {candidates.length} path{candidates.length > 1 ? 's' : ''}):{' '}
            {candidates[candidates.length - 1]}
          </BrokenBadge>
        )}
        <Img
          src={src}
          alt={project.name}
          $loaded={loaded}
          $clickable={hasLink}
          onLoad={() => setLoaded(true)}
          onError={() => {
            const nextIndex = attemptIndex + 1;
            if (nextIndex < candidates.length) {
              // Try the next candidate shape before giving up.
              console.warn(
                `[ProjectCard] "${src}" failed, trying next candidate:`,
                candidates[nextIndex],
              );
              setAttemptIndex(nextIndex);
              return;
            }
            console.warn('[ProjectCard] all candidate URLs failed:', {
              candidates,
              project,
            });
            setBroken(true);
            setLoaded(true);
          }}
        />
        {hasLink && (
          <LinkOverlay className="link-overlay">
          </LinkOverlay>
        )}
      </CardImg>
      <CardBody>
        <CardTitle>{project.name}</CardTitle>
        <CardDesc>{project.description || 'No description.'}</CardDesc>
        {project.link && (
          <CardLink href={project.link} target="_blank" rel="noopener noreferrer">
            {project.link}
          </CardLink>
        )}
        <CardActions>
          <ActionBtn $variant="edit"   onClick={() => onEdit(project)}>Edit</ActionBtn>
          <ActionBtn $variant="delete" onClick={() => onDelete(project)}>Delete</ActionBtn>
        </CardActions>
      </CardBody>
    </Card>
  );
}

/* ─── Main Page ─── */
export default function AdminProjects() {
  const [projects,    setProjects]    = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [error,       setError]       = useState(null);
  const [modal,       setModal]       = useState(null); // 'add' | 'edit' | 'delete'
  const [selected,    setSelected]    = useState(null);
  const [saving,      setSaving]      = useState(false);
  const [formErr,     setFormErr]     = useState('');
  const [name,        setName]        = useState('');
  const [description, setDescription] = useState('');
  const [link,        setLink]        = useState('');
  const [imageFile,   setImageFile]   = useState(null);
  const [preview,     setPreview]     = useState(null);
  const fileRef = useRef();

  /* ─── Fetch ─── */
  const fetchProjects = async () => {
    try {
      setLoading(true);
      setError(null);
      const { data } = await Config.get('/application');
      if (!Array.isArray(data)) throw new Error('Bad response shape');
      // Log the raw shape once so you can confirm the actual image field
      // name / id in the console, same as done for client logos.
      if (data[0]) console.log('[AdminProjects] sample project record:', data[0]);
      setProjects(data);
    } catch (err) {
      console.error('Fetch error:', err);
      setError('Could not load projects.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchProjects(); }, []);

  /* ─── File change (with validation) ─── */
  const handleFileChange = (e) => {
    const file = e.target.files[0] || null;
    setFormErr('');

    if (!file) {
      setImageFile(null);
      setPreview(null);
      return;
    }

    if (!file.type.startsWith('image/')) {
      setFormErr('Please select a valid image file.');
      e.target.value = '';
      return;
    }

    if (file.size > MAX_FILE_SIZE) {
      setFormErr('Image must be smaller than 5MB.');
      e.target.value = '';
      return;
    }

    setImageFile(file);
    const reader = new FileReader();
    reader.onload = (ev) => setPreview(ev.target.result);
    reader.readAsDataURL(file);
  };

  /* ─── URL validation (link is optional) ─── */
  const isValidUrl = (value) => {
    if (!value.trim()) return true;
    try {
      new URL(value.trim());
      return true;
    } catch {
      return false;
    }
  };

  /* ─── Modals ─── */
  const openAdd = () => {
    setName(''); setDescription(''); setLink(''); setImageFile(null);
    setPreview(null); setFormErr(''); setSelected(null);
    setModal('add');
  };

  const openEdit = (project) => {
    setName(project.name || '');
    setDescription(project.description || '');
    setLink(project.link || '');
    setImageFile(null);
    // Use the first candidate URL as the initial preview; if it 404s the
    // <img>'s own onError below swaps it to DEFAULT_IMAGE.
    setPreview(project.id ? buildImageCandidates(project)[0] : null);
    setFormErr('');
    setSelected(project);
    setModal('edit');
  };

  const openDelete = (project) => {
    setSelected(project);
    setFormErr('');
    setModal('delete');
  };

  const closeModal = () => {
    setModal(null);
    setSelected(null);
    setSaving(false);
    setFormErr('');
    setPreview(null);
    setImageFile(null);
    setLink('');
    if (fileRef.current) fileRef.current.value = '';
  };

  /* ─── Add ─── */
  // Confirmed via Swagger: name, description & link are query params,
  // image is multipart/form-data body, and image & link are both optional.
  //
  // FIX: do NOT set `Content-Type: multipart/form-data` manually. When you
  // send a FormData body, the browser/axios needs to append its own
  // boundary string (e.g. `multipart/form-data; boundary=----WebKit...`).
  // If you set the header yourself without a boundary, axios sees the
  // header is already present and won't override it — the server then
  // receives a body it can't parse, so the request silently "does
  // nothing" or 400s. Leaving the header out lets axios set it correctly.
  const handleAdd = async () => {
    if (!name.trim()) return setFormErr('Project name is required.');
    if (!isValidUrl(link)) return setFormErr('Please enter a valid URL (e.g. https://example.com).');
    setSaving(true);
    setFormErr('');
    try {
      const fd = new FormData();
      if (imageFile) fd.append('image', imageFile);
      const res = await Config.post('/application', fd, {
        params: { name: name.trim(), description: description.trim(), link: link.trim() },
        // no headers override — let axios set the multipart boundary itself
      });
      // Log what the backend returns right after upload — useful for
      // confirming the id/field the image is later served under.
      console.log('[AdminProjects] upload response:', res?.data);
      await fetchProjects();
      closeModal();
    } catch (err) {
      console.error('Add error:', err?.response?.status, err?.response?.data || err.message);
      setFormErr(
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        'Failed to add project. Please check with the backend team.'
      );
    } finally {
      setSaving(false);
    }
  };

  /* ─── Edit ─── */
  const handleEdit = async () => {
    if (!name.trim()) return setFormErr('Project name is required.');
    if (!isValidUrl(link)) return setFormErr('Please enter a valid URL (e.g. https://example.com).');
    setSaving(true);
    setFormErr('');
    try {
      const fd = new FormData();
      if (imageFile) fd.append('image', imageFile);
      await Config.put(`/application/${selected.id}`, fd, {
        params: { name: name.trim(), description: description.trim(), link: link.trim() },
        // no headers override — same reasoning as handleAdd above
      });
      await fetchProjects();
      closeModal();
    } catch (err) {
      console.error('Edit error:', err?.response?.status, err?.response?.data || err.message);
      setFormErr(
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        'Failed to update project. Please check with the backend team.'
      );
    } finally {
      setSaving(false);
    }
  };

  /* ─── Delete ─── */
  const handleDelete = async () => {
    setSaving(true);
    setFormErr('');
    try {
      await Config.delete(`/application/${selected.id}`);
      await fetchProjects();
      closeModal();
    } catch (err) {
      console.error('Delete error:', err?.response?.data || err.message);
      setFormErr('Failed to delete project.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Page>
      <TopBar>
        <PageTitle>Manage Projects</PageTitle>
        <AddBtn onClick={openAdd}>+ Add Project</AddBtn>
      </TopBar>

      {error && <StatusText $error>{error}</StatusText>}

      {loading ? (
        <Grid>
          {Array.from({ length: 6 }, (_, i) => (
            <SkeletonCard key={i}>
              <div className="sk-img" />
              <div className="sk-body">
                <div className="sk-line" style={{ width: '55%' }} />
                <div className="sk-line" style={{ width: '85%' }} />
                <div className="sk-line" style={{ width: '70%' }} />
              </div>
            </SkeletonCard>
          ))}
        </Grid>
      ) : !error && projects.length === 0 ? (
        <StatusText>No projects yet. Click "+ Add Project" to get started.</StatusText>
      ) : (
        <Grid>
          <AnimatePresence>
            {projects.map((p) => (
              <ProjectCard
                key={p.id}
                project={p}
                onEdit={openEdit}
                onDelete={openDelete}
              />
            ))}
          </AnimatePresence>
        </Grid>
      )}

      {/* ── Add / Edit Modal ── */}
      <AnimatePresence>
        {(modal === 'add' || modal === 'edit') && (
          <Overlay
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={(e) => e.target === e.currentTarget && closeModal()}
          >
            <Modal
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
            >
              <ModalTitle>
                {modal === 'add' ? 'Add Project' : 'Edit Project'}
              </ModalTitle>

              <Field>
                <Label>Project Name *</Label>
                <Input
                  placeholder="e.g. Employee Management"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />
              </Field>

              <Field>
                <Label>Description</Label>
                <Textarea
                  placeholder="Brief description of the project..."
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                />
              </Field>

              <Field>
                <Label>Project Link</Label>
                <Input
                  type="url"
                  placeholder="https://example.com"
                  value={link}
                  onChange={(e) => setLink(e.target.value)}
                />
              </Field>

              <Field>
                <Label>{modal === 'edit' ? 'Replace Image' : 'Image'}</Label>
                <FileInput
                  type="file"
                  accept="image/*"
                  ref={fileRef}
                  onChange={handleFileChange}
                />
                {preview && (
                  <PreviewImg
                    src={preview}
                    alt="preview"
                    onError={(e) => { e.currentTarget.src = DEFAULT_IMAGE; }}
                  />
                )}
              </Field>

              {formErr && <ErrMsg>{formErr}</ErrMsg>}

              <ModalActions>
                <ModalBtn onClick={closeModal} disabled={saving}>
                  Cancel
                </ModalBtn>
                <ModalBtn
                  $primary
                  onClick={modal === 'add' ? handleAdd : handleEdit}
                  disabled={saving}
                >
                  {saving
                    ? 'Saving...'
                    : modal === 'add' ? 'Add Project' : 'Save Changes'}
                </ModalBtn>
              </ModalActions>
            </Modal>
          </Overlay>
        )}
      </AnimatePresence>

      {/* ── Delete Confirm Modal ── */}
      <AnimatePresence>
        {modal === 'delete' && (
          <Overlay
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={(e) => e.target === e.currentTarget && closeModal()}
          >
            <Modal
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
            >
              <ModalTitle>Delete Project</ModalTitle>
              <ConfirmText>
                Are you sure you want to delete{' '}
                <strong style={{ color: '#f1f5f9' }}>{selected?.name}</strong>?
                This action cannot be undone.
              </ConfirmText>

              {formErr && <ErrMsg>{formErr}</ErrMsg>}

              <ModalActions>
                <ModalBtn onClick={closeModal} disabled={saving}>
                  Cancel
                </ModalBtn>
                <ModalBtn
                  $primary
                  onClick={handleDelete}
                  disabled={saving}
                  style={{ background: 'linear-gradient(135deg,#dc2626,#f87171)' }}
                >
                  {saving ? 'Deleting...' : 'Yes, Delete'}
                </ModalBtn>
              </ModalActions>
            </Modal>
          </Overlay>
        )}
      </AnimatePresence>
    </Page>
  );
}